import { ActionModal, Checkbox, FormError } from '@kcftech/design-system';
import {
    IHardwareNotificationSubscriptionResponse,
    IHardwareNotificationTriggerRequestChild,
} from '@kcftech/prometheus-client';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useEditHardwareNotificationSubscription } from 'custom_hooks/data_hooks/notification_subscriptions/useEditHardwareNotificationSubscription';
import { useSpecificUserInfo } from 'custom_hooks/data_hooks/user/useSpecificUserInfo';
import { EditHardwareNotificationSubscriptionFormData } from 'pages/Settings/NotificationSubscriptions/form/types';
import {
    NotificationTriggerTable,
    NotificationTriggerTableWrapper,
} from 'pages/Settings/NotificationSubscriptions/NotificationTriggerTable';
import {
    hasEnabledNotificationMethods,
    notificationHardwareTypeName,
    ValidNotificationHardwareType,
    validateNotificationTriggers,
    validNotificationHardwareTypes,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
    Controller,
    UseFormReturn,
    useForm,
    useWatch,
    useFormState,
} from 'react-hook-form';

export type EditHardwareNotificationSubscriptionModalProps = Readonly<{
    notificationSubscription: IHardwareNotificationSubscriptionResponse;
    onClose: (saved: boolean) => void;
}>;

const responseToFormData = (
    response: IHardwareNotificationSubscriptionResponse,
): EditHardwareNotificationSubscriptionFormData => ({
    isSilenced: response.IsSilenced,
    notificationTriggers: Object.fromEntries(
        withMissingHardwareTriggers(response.NotificationTriggers).map((nt) => [
            nt.HardwareType,
            nt,
        ]),
    ) as {
        [key in ValidNotificationHardwareType]: IHardwareNotificationTriggerRequestChild;
    },
});

/**
 * A Subscription doesn't _necessarily_ contain all possible triggers. We
 * display all possibilities with checkboxes in the UI, though. This adds the
 * necessary triggers as needed (They will later be removed before we serialize
 * the request, if unused)
 */
const withMissingHardwareTriggers = (
    existing: IHardwareNotificationTriggerRequestChild[],
): IHardwareNotificationTriggerRequestChild[] => [
    ...existing,
    ...validNotificationHardwareTypes
        .filter((type) => !existing.some((nt) => nt.HardwareType === type))
        .map((type) => ({
            HardwareType: type,
            IsEmailEnabled: false,
            IsSmsEnabled: false,
        })),
];

export function EditHardwareNotificationSubscriptionModal({
    notificationSubscription,
    onClose,
}: EditHardwareNotificationSubscriptionModalProps) {
    const { showToast } = useNotificationContext();
    const notificationTriggerErrorState = useState<string | undefined>(undefined);

    const user = useSpecificUserInfo(notificationSubscription.User.UserId);
    const userHasPhoneNumber = user.data?.PhoneNumber !== undefined;

    const form = useForm<EditHardwareNotificationSubscriptionFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        defaultValues: responseToFormData(notificationSubscription),
    });

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const {
        mutate: editHardwareNotificationSubscription,
        isPending: isEditingHardware,
    } = useEditHardwareNotificationSubscription({
        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully updated hardware notification subscription',
            });
        },
        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update hardware notification subscription',
            }),
    });

    const onEditHardware = handleSubmit((formData) => {
        if (
            validateNotificationTriggers(
                Object.values(formData.notificationTriggers),
                userHasPhoneNumber,
            ) !== undefined
        ) {
            return; // this is an error, handeled by notificationTriggerErrorState
        }
        editHardwareNotificationSubscription({
            id: notificationSubscription.Id,
            isSilenced: formData.isSilenced,
            triggers: Object.values(formData.notificationTriggers).filter(
                (nt) => !!nt.NotificationTriggerId || hasEnabledNotificationMethods(nt),
            ),
        });
    });

    return (
        <ActionModal
            title="Edit Hardware Notification Subscription"
            actionable={
                (!isSubmitted || (isValid && !notificationTriggerErrorState[0])) &&
                !(isValidating || isSubmitting || isEditingHardware)
            }
            onAction={onEditHardware}
            onClose={() => onClose(false)}
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={384}
        >
            <HardwareTriggersEditor
                form={form}
                errorState={notificationTriggerErrorState}
                userHasPhoneNumber={userHasPhoneNumber}
            />
        </ActionModal>
    );
}

function HardwareTriggersEditor({
    form,
    errorState,
    userHasPhoneNumber,
}: {
    form: UseFormReturn<EditHardwareNotificationSubscriptionFormData>;
    errorState: [string | undefined, Dispatch<SetStateAction<string | undefined>>];
    userHasPhoneNumber: boolean;
}) {
    const {
        control,
    } = form;

    const {
        isSubmitted,
    } = useFormState({
        control,
    });

    const notificationTriggers = useWatch({ control, name: 'notificationTriggers' });

    const [error, setError] = errorState;

    useEffect(() => {
        setError(
            validateNotificationTriggers(
                Object.values(notificationTriggers),
                userHasPhoneNumber,
            ),
        );
    }, [notificationTriggers, setError, userHasPhoneNumber]);

    const rowForHardwareType = (hardwareType: ValidNotificationHardwareType) => (
        <tr key={hardwareType}>
            <td>{notificationHardwareTypeName[hardwareType]}</td>
            <td className="text-right">
                <Controller
                    control={control}
                    name={`notificationTriggers.${hardwareType}.IsEmailEnabled` as const}
                    render={({ field: { value, onChange } }) => (
                        <Checkbox
                            label=""
                            onChange={onChange}
                            checked={value}
                            title={`Toggle emails for ${notificationHardwareTypeName[hardwareType]}`}
                        />
                    )}
                />
            </td>
            <td className="text-right">
                <Controller
                    control={control}
                    name={`notificationTriggers.${hardwareType}.IsSmsEnabled` as const}
                    render={({ field: { value, onChange } }) => (
                        <Checkbox
                            label=""
                            onChange={onChange}
                            checked={value}
                            disabled
                            title="Text notifications are not yet available for Hardware Alerts"
                            // TODO: Uncomment after sms is implemented.
                            // title={`Toggle text messages for ${notificationHardwareType[hardwareType]}`}
                        />
                    )}
                />
            </td>
        </tr>
    );

    return (
        <NotificationTriggerTableWrapper>
            <NotificationTriggerTable
                rows={validNotificationHardwareTypes.map(rowForHardwareType)}
            />
            <FormError message={isSubmitted ? error : undefined} />
        </NotificationTriggerTableWrapper>
    );
}
